/*
 * Copyright (C) 2026 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { formatDate } from '@angular/common';
import {
  V3ContentCardAction,
  V3ContentCardItem,
  V3ContentCardMetaEntry,
  V3ContentCardStatusBadge,
  v3ContentCardGradient,
} from '@app/app/shared/components-v3/content-card/v3-content-card.model';
import { contentCardTypeLabel } from '@app/app/shared/helpers/content-card-type-label.helper';
import { ContentHelper } from '@app/app/shared/helpers/content-helper';
import { AssignmentItemBadge, PlaylistCardShort } from '@app/app/shared/models';
import {
  LucideBookOpen,
  LucideCalendar,
  LucideCalendarDays,
  LucideCheck,
  LucideClipboardCheck,
  LucideIconInput,
  LucideLayers,
  LucideLock,
  LucideSparkles,
} from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';

export function mapPlaylistCardToContentCardItem(
  card: PlaylistCardShort,
  cardIndex: number,
  transloco: TranslocoService,
): V3ContentCardItem {
  if (isAssignmentCard(card)) {
    return mapAssignmentCardToContentCardItem(card, cardIndex, transloco);
  }
  const progressPercentage = playlistCardProgressPercentage(card);
  const completed = isPlaylistCardCompleted(card);
  const inProgress = !completed && (card.started || progressPercentage > 0);
  const isLtiCourse = isLtiCourseCard(card);
  const showScoreAsGrade = (isLtiCourse || isProgramCard(card)) && (inProgress || completed);
  const showProgress = !isLtiCourse && !isProgramCard(card) && !isEventCard(card) && inProgress;
  const gradeLabel = showScoreAsGrade ? transloco.translate('translations.cardItem.grade', { value: progressPercentage }) : null;
  return {
    uid: card._id,
    title: card.header?.trim() || playlistCardUntitledLabel(card, transloco),
    titleIsPlaceholder: !card.header?.trim(),
    thumbnail: card.image || null,
    gradient: v3ContentCardGradient(card._id, cardIndex),
    coverIcon: !isEventCard(card) && !completed && !inProgress ? LucideLock : null,
    typeLabel: playlistCardTypeLabel(card, transloco),
    typeIcon: playlistCardTypeIcon(card),
    progress: showProgress ? progressPercentage : null,
    gradeLabel,
    gradePassing: progressPercentage >= 60,
    // An indicator card carries no completed badge: the badge row is one the other cards never grow,
    // so it would leave the indicator taller than everything beside it in the same row.
    statusBadge:
      completed && !isIndicatorCard(card)
        ? { label: transloco.translate('translations.cardItem.completed'), tone: 'success', icon: LucideCheck }
        : null,
  };
}

export function mapPlaylistCardToContentCardAction(card: PlaylistCardShort, transloco: TranslocoService): V3ContentCardAction | null {
  if (isEventCard(card)) {
    return { label: transloco.translate('translations.eventLearnerView.register'), type: 'primary' };
  }
  if (isAssignmentCard(card)) {
    // Learner CTA mirrors the submission chip; the admin audience gets none.
    switch (card.assignmentItemState?.badge) {
      case 'NOT_SUBMITTED':
        return { label: transloco.translate('translations.cardItem.start'), type: 'primary' };
      case 'UNDER_REVIEW':
      case 'RETURNED':
        return { label: transloco.translate('translations.cardItem.continue'), type: 'primary' };
      default:
        return null;
    }
  }
  if (isPlaylistCardCompleted(card)) {
    return null;
  }
  const progressPercentage = playlistCardProgressPercentage(card);
  return card.started || progressPercentage > 0
    ? { label: transloco.translate('translations.cardItem.continue'), type: 'primary' }
    : { label: transloco.translate('translations.cardItem.start'), type: 'primary' };
}

export function openPlaylistCard(card: PlaylistCardShort, emit: (card: PlaylistCardShort) => void): void {
  emit(card);
}

function playlistCardUntitledLabel(card: PlaylistCardShort, transloco: TranslocoService): string {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  switch (type) {
    case 'COURSE':
    case 'LTI_COURSE':
      return transloco.translate('translations.cardItem.untitled.course');
    case 'PROGRAM':
    case 'MODULE':
      return transloco.translate('translations.cardItem.untitled.programme');
    case 'EVENT':
      return transloco.translate('translations.cardItem.untitled.event');
    case 'ASSIGNMENT':
      return transloco.translate('translations.cardItem.untitled.assignment');
    case 'RESOURCE':
      return transloco.translate('translations.cardItem.untitled.resource');
    case 'ASSESSMENT':
      return transloco.translate('translations.cardItem.untitled.assessment');
    default:
      return transloco.translate('translations.cardItem.untitled.card');
  }
}

function playlistCardTypeLabel(card: PlaylistCardShort, transloco: TranslocoService): string {
  const type = card?.v3CardType || card?.collectionType;
  if (type?.toUpperCase() === 'LTI_COURSE') {
    if (card?.lmsPlatformType === 'CANVAS') {
      return transloco.translate('translations.cardItem.type.CANVAS_COURSE');
    }
    return transloco.translate('translations.cardItem.type.LTI_COURSE');
  }
  return contentCardTypeLabel(transloco, card?.v3CardType, card?.collectionType, card?.type);
}

function playlistCardTypeIcon(card: PlaylistCardShort): LucideIconInput | null {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  if (!type) {
    return null;
  }
  if (type === 'EVENT') {
    return LucideCalendar;
  }
  if (type === 'ASSIGNMENT') {
    return LucideClipboardCheck;
  }
  if (type === 'MODULE' || type === 'PROGRAM') {
    return LucideLayers;
  }
  if (type === 'PERSONALITY_INDICATOR') {
    return LucideSparkles;
  }
  return LucideBookOpen;
}

function isAssignmentCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType)?.toUpperCase() === 'ASSIGNMENT' || card?.type === 'ASSIGNMENT';
}

/**
 * Assignment section items: thumbnail/title/blurb as usual, but no LearnerCard progress or lock
 * cover (assignment completion is gradebook-tracked, not card-completion-tracked). The state chip is the
 * viewer-specific badge the course content tab merged in (learner submission state / admin Draft-Published).
 */
function mapAssignmentCardToContentCardItem(card: PlaylistCardShort, cardIndex: number, transloco: TranslocoService): V3ContentCardItem {
  const state = card.assignmentItemState;
  return {
    uid: card._id,
    title: card.header?.trim() || playlistCardUntitledLabel(card, transloco),
    titleIsPlaceholder: !card.header?.trim(),
    // The blurb is the card's authored rich content; `subtitle` is interpolated, so flatten it or the tags show.
    subtitle: ContentHelper.toPlainText(card.blurb) || undefined,
    thumbnail: card.image || null,
    gradient: v3ContentCardGradient(card._id, cardIndex),
    coverIcon: LucideClipboardCheck,
    typeLabel: playlistCardTypeLabel(card, transloco),
    typeIcon: LucideClipboardCheck,
    statusBadge: state ? assignmentStatusBadge(state.badge, transloco) : null,
    meta: assignmentDeadlineMeta(state?.deadline, transloco),
  };
}

function assignmentStatusBadge(badge: AssignmentItemBadge, transloco: TranslocoService): V3ContentCardStatusBadge {
  switch (badge) {
    case 'DRAFT':
      return { label: transloco.translate('translations.cardItem.status.draft'), tone: 'draft' };
    case 'PUBLISHED':
      return { label: transloco.translate('translations.cardItem.status.published'), tone: 'success' };
    case 'ARCHIVED':
      return { label: transloco.translate('translations.cardItem.status.archived'), tone: 'archived' };
    case 'UNDER_REVIEW':
      return { label: transloco.translate('translations.courses.mySubmissions.status.submitted'), tone: 'neutral' };
    case 'RETURNED':
      return { label: transloco.translate('translations.courses.mySubmissions.status.revisionsRequested'), tone: 'warning' };
    case 'RELEASED':
      return { label: transloco.translate('translations.courses.mySubmissions.status.marked'), tone: 'success', icon: LucideCheck };
    default:
      return { label: transloco.translate('translations.cardItem.notStarted'), tone: 'neutral' };
  }
}

function assignmentDeadlineMeta(deadline: string | null | undefined, transloco: TranslocoService): V3ContentCardMetaEntry[] | undefined {
  if (!deadline) {
    return undefined;
  }
  // 'en-US' matches the app-wide date-pipe output (no LOCALE_ID override is registered).
  const date = formatDate(deadline, 'd MMM', 'en-US');
  return [{ icon: LucideCalendarDays, text: transloco.translate('translations.courses.mySubmissions.due', { date }) }];
}

function isLtiCourseCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType)?.toUpperCase() === 'LTI_COURSE';
}

function isProgramCard(card: PlaylistCardShort): boolean {
  const type = (card?.v3CardType || card?.collectionType)?.toUpperCase();
  return type === 'PROGRAM' || type === 'MODULE';
}

function isEventCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType || card?.type)?.toUpperCase() === 'EVENT';
}

function isIndicatorCard(card: PlaylistCardShort): boolean {
  return (card?.v3CardType || card?.collectionType)?.toUpperCase() === 'PERSONALITY_INDICATOR';
}

// For LTI courses the percentage is a grade, not progress — 100% there must not imply completion.
function isPlaylistCardCompleted(card: PlaylistCardShort): boolean {
  return !!card?.completed || (!isLtiCourseCard(card) && playlistCardProgressPercentage(card) >= 100);
}

function playlistCardProgressPercentage(card: PlaylistCardShort): number {
  const completed = card?.completedItemsCount ?? 0;
  const total = card?.totalItemsCount ?? 0;
  if (total > 0) {
    return Math.round((completed / total) * 100);
  }
  return card?.completed ? 100 : 0;
}
